import {Component, Input} from '@angular/core';
import {CommonModule} from '@angular/common';
import dayjs from 'dayjs';
import {WeeklyDailyCheckinsRollupV2} from '@shared/api/models/query/patient-weekly-snapshot.model';
import {WeeklyTrendSparklineComponent} from '@cdss/pages/patient-overview-v2/detail/weekly/components/weekly-trend-sparkline/weekly-trend-sparkline.component';
import {WeeklyStateLabelComponent} from '@cdss/pages/patient-overview-v2/detail/weekly/components/weekly-state-label/weekly-state-label.component';
import {EmotionIconComponent} from '@shared/components/emotion-icon/emotion-icon.component';

/**
 * Patient (and carer, sub-task 7 / UNIVAPLAT-3039) weekly summary — "Daily check-ins
 * this week" panel (UNIVAPLAT-3036), rendering the v2 `dailyCheckins` weekly roll-up
 * (`WeeklyDailyCheckinsRollupV2`) — never the retired per-day map that
 * `cdss-weekly-daily-checkins-table` / `cdss-weekly-daily-trends` used to render.
 *
 * Shared as-is between the patient and carer pages (identical roll-up shape per
 * UNIVAPLAT-2962 OQ-FE1) — rendering never differs between them (UNIVAPLAT-2360
 * follow-up: row labels were simplified to "Sleep"/"Mood"/"ED behaviours", dropping
 * the carer-only "Observed ED behaviours" copy variant along with the now-unused
 * `variant` input that drove it). The one exception is `sleepMoodTitle` (added in a
 * later UNIVAPLAT-2360 follow-up): a plain heading-text override, not a rendering
 * branch — the carer page sets it to "Observed behaviours".
 *
 * Left: a stress trend graph across the week's 7 days (`stress.values`, Ymd-keyed).
 * A `null` day is a gap — excluded from the backend-computed `average` and never
 * plotted as 0 (the recurring HAZ-W2 theme across this Task). `average`/`peak` are
 * both absent together when no day was rated, rendering "No stress ratings this
 * week" via the shared five-state label instead of a chart with no data.
 *
 * Right ("Sleep & mood"): three independently-gated lines — tired/exhausted days,
 * most-common mood, and ED behaviour events — each falling back to its own no-data
 * copy when its respective `answeredDays`/`mostCommon` is absent, per the ticket's
 * acceptance criteria. The behaviours line additionally distinguishes "no behaviour
 * answers at all" from an affirmative "answered None every day" using
 * `edBehaviourAnsweredDays` (UNIVAPLAT-3055, UNIVAPLAT-2962 OQ-FE6):
 *   - `edBehaviourAnsweredDays === 0` → not-rated / no-data state ("No behaviours
 *     reported").
 *   - `edBehaviourAnsweredDays > 0 && edBehaviourCounter === 0` → the affirmative
 *     "Nothing to report" state — clinically distinct from missing data.
 *   - otherwise → the formatted event count.
 *
 * The stress graph's per-day click-through to `patient-daily` (previously wired via
 * the retired table's `dayClick` output) was NOT carried over: the shared
 * `cdss-weekly-trend-sparkline` primitive has no click output and its dots are only
 * a 2px radius — adding a click target there is a shared-component change with its
 * own accessibility/hit-area work, not a cheap addition within this sub-task's scope
 * (UNIVAPLAT-2962 ticket text explicitly allows dropping it and noting so — done here
 * and in the PR).
 *
 */
@Component({
    selector: 'cdss-weekly-daily-checkins-rollup',
    standalone: true,
    imports: [CommonModule, WeeklyTrendSparklineComponent, WeeklyStateLabelComponent, EmotionIconComponent],
    templateUrl: './weekly-daily-checkins-rollup.component.html',
    styleUrl: './weekly-daily-checkins-rollup.component.scss'
})
export class WeeklyDailyCheckinsRollupComponent {

    @Input() dailyCheckins: WeeklyDailyCheckinsRollupV2 | null = null;

    /**
     * The one page-level copy override this component accepts (UNIVAPLAT-2360 follow-up) — the
     * carer page passes "Observed behaviours" instead of the default. This is a plain title swap,
     * not the removed `variant` input: it changes no rendering logic, only this one heading's text.
     */
    @Input() sleepMoodTitle = 'Sleep & mood';

    get hasStressData(): boolean {
        return this.dailyCheckins?.stress?.average !== undefined
            && this.dailyCheckins?.stress?.peak !== undefined;
    }

    /** Chronological (Ymd keys sort correctly as strings), `null` = gap — never plotted as 0. */
    get stressPoints(): (number | null)[] {
        const values = this.dailyCheckins?.stress?.values;
        if (!values) return [];
        return Object.keys(values).sort().map(k => values[k]);
    }

    get stressPointLabels(): string[] {
        const values = this.dailyCheckins?.stress?.values;
        if (!values) return [];
        return Object.keys(values).sort().map(k => this.weekdayFromYmd(k));
    }

    get stressAverageLabel(): string {
        const average = this.dailyCheckins?.stress?.average;
        if (average === undefined) return '';
        return Number.isInteger(average) ? `${average}` : average.toFixed(1);
    }

    get stressPeakLabel(): string {
        const peak = this.dailyCheckins?.stress?.peak;
        if (!peak) return '';
        return `${peak.value} on ${peak.weekday}`;
    }

    get sleepAnswered(): boolean {
        return (this.dailyCheckins?.sleep?.answeredDays ?? 0) > 0;
    }

    get sleepLabel(): string {
        const tiredOrExhaustedDays = this.dailyCheckins?.sleep?.tiredOrExhaustedDays ?? 0;
        const answeredDays = this.dailyCheckins?.sleep?.answeredDays ?? 0;
        return `Tired or exhausted on waking: ${tiredOrExhaustedDays} of ${answeredDays} days`;
    }

    get moodAnswered(): boolean {
        return this.dailyCheckins?.mood?.mostCommon !== undefined;
    }

    get moodEmoji(): string | null {
        return this.dailyCheckins?.mood?.mostCommon ?? null;
    }

    get moodLabel(): string {
        const mood = this.dailyCheckins?.mood;
        if (!mood || mood.mostCommon === undefined) return '';
        const count = mood.count ?? 0;
        return `${count} ${mood.mostCommon}`;
    }

    /** Five-state matrix per UNIVAPLAT-2962 OQ-FE6 — see class doc. */
    get behavioursState(): 'noData' | 'nothingToReport' | 'count' {
        const answeredDays = this.dailyCheckins?.edBehaviourAnsweredDays ?? 0;
        const counter = this.dailyCheckins?.edBehaviourCounter ?? 0;
        if (answeredDays === 0) return 'noData';
        if (counter === 0) return 'nothingToReport';
        return 'count';
    }

    get behavioursCountLabel(): string {
        const counter = this.dailyCheckins?.edBehaviourCounter ?? 0;
        return `${counter} ${counter === 1 ? 'event' : 'events'}`;
    }

    private weekdayFromYmd(key: string): string {
        const iso = key.length === 8
            ? `${key.substring(0, 4)}-${key.substring(4, 6)}-${key.substring(6, 8)}`
            : key;
        return dayjs(iso).format('ddd');
    }
}
